import { useState, useRef, useEffect } from "react";
import { Globe, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LANGS } from "@/i18n/translations";
import { useLang } from "@/i18n/LanguageContext";

export const LanguageSwitcher = ({ align = "right" }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher"
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold text-white hover:border-emerald-400/50 transition-colors"
      >
        <Globe className="h-4 w-4 text-emerald-400" />
        {current.label}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className={`absolute mt-2 min-w-[150px] rounded-xl border border-white/10 bg-[#0d1210] p-1.5 shadow-2xl z-[70] ${
              align === "right" ? "right-0" : "left-0"
            }`}
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                data-testid={`lang-option-${l.code}`}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  lang === l.code
                    ? "bg-emerald-500/10 text-emerald-300"
                    : "text-slate-300 hover:bg-white/5"
                }`}
              >
                <span>{l.name}</span>
                {lang === l.code && <Check className="h-4 w-4" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
